import React from 'react';
import { Link } from 'react-router-dom';
import { siteBranding, navigationMenu } from '../data';
import { getResizedImageUrl } from '../lib/utils';

export default function Footer() {
  return (
    <footer className="bg-brand-light-gold border-t border-brand-primary/10 text-brand-text">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          
          {/* Brand & About Column */}
          <div className="flex flex-col space-y-5">
            <div className="flex items-center gap-3.5">
              <img
                src={getResizedImageUrl(siteBranding.logo, "width=120,quality=85,format=auto")}
                alt="Moon Yoga Logo"
                className="h-12 w-auto object-contain"
                width="120"
                height="48"
                loading="lazy"
              />
              <span className="font-serif text-xl tracking-wide text-brand-text font-medium">
                Moon Yoga Fredericia
              </span>
            </div>
            <p className="text-sm text-brand-muted leading-relaxed">
              Et nærværende frirum skabt til dig, der længes efter ro, balance og fordybelse i hverdagen. Vi glæder os til at møde dig på måtten.
            </p>
            <div className="text-xs text-brand-muted/80 pt-1">
              <span className="font-medium">CVR:</span> {siteBranding.cvr}
            </div>
          </div>

          {/* Navigation Column */}
          <div className="flex flex-col space-y-5">
            <h3 className="font-serif text-lg font-semibold tracking-wide text-brand-primary">
              Genveje
            </h3>
            <ul className="space-y-3">
              {navigationMenu.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="text-sm text-brand-text/80 hover:text-brand-primary transition-colors duration-300 flex items-center gap-2.5 group"
                  >
                    <i className="fa-solid fa-chevron-right text-[10px] text-brand-primary/40 group-hover:text-brand-primary/80 transition-colors" />
                    <span className="group-hover:translate-x-0.5 transition-transform duration-300">
                      {item.label}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links Column */}
          <div className="flex flex-col space-y-5">
            <h3 className="font-serif text-lg font-semibold tracking-wide text-brand-primary">
              Betingelser & Privatliv
            </h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/privatlivspolitik"
                  className="text-sm text-brand-text/80 hover:text-brand-primary transition-colors duration-300 flex items-center gap-2.5 group"
                >
                  <i className="fa-solid fa-shield-halved text-[10px] text-brand-primary/40 group-hover:text-brand-primary/80 transition-colors" />
                  <span className="group-hover:translate-x-0.5 transition-transform duration-300">
                    Privatlivspolitik
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/handelsbetingelser"
                  className="text-sm text-brand-text/80 hover:text-brand-primary transition-colors duration-300 flex items-center gap-2.5 group"
                >
                  <i className="fa-solid fa-file-contract text-[10px] text-brand-primary/40 group-hover:text-brand-primary/80 transition-colors" />
                  <span className="group-hover:translate-x-0.5 transition-transform duration-300">
                    Handels- & forretningsbetingelser
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/tilmeldingsbetingelser"
                  className="text-sm text-brand-text/80 hover:text-brand-primary transition-colors duration-300 flex items-center gap-2.5 group"
                >
                  <i className="fa-solid fa-clipboard-check text-[10px] text-brand-primary/40 group-hover:text-brand-primary/80 transition-colors" />
                  <span className="group-hover:translate-x-0.5 transition-transform duration-300">
                    Tilmeldingsbetingelser
                  </span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Column */}
          <div className="flex flex-col space-y-5">
            <h3 className="font-serif text-lg font-semibold tracking-wide text-brand-primary">
              Kontakt & Find os
            </h3>
            <ul className="space-y-3.5 text-sm text-brand-text/80">
              <li className="flex items-start gap-3">
                <i className="fa-solid fa-location-dot mt-1 text-brand-primary/60" />
                <div>
                  <span className="block font-medium text-brand-text">Moon Yoga ApS</span>
                  <a
                    href="https://maps.google.com/?q=Dalegade+61B,+7000+Fredericia"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-brand-primary transition-colors duration-300"
                  >
                    {siteBranding.address}
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-phone text-brand-primary/60" />
                <a
                  href={`tel:+45${siteBranding.phone.replace(/\s+/g, '')}`}
                  className="hover:text-brand-primary transition-colors duration-300"
                >
                  {siteBranding.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-envelope text-brand-primary/60" />
                <a
                  href={`mailto:${siteBranding.email}`}
                  className="hover:text-brand-primary transition-colors duration-300 break-all"
                >
                  {siteBranding.email}
                </a>
              </li>
            </ul>

            {/* Social Media Links */}
            <div className="pt-3 flex items-center gap-3 border-t border-brand-primary/5">
              <a
                href={siteBranding.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="h-9 w-9 rounded-full bg-brand-primary/5 hover:bg-brand-primary hover:text-brand-bg text-brand-primary flex items-center justify-center transition-all duration-300"
                aria-label="Følg os på Facebook"
              >
                <i className="fa-brands fa-facebook-f text-base" />
              </a>
              <a
                href={siteBranding.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="h-9 w-9 rounded-full bg-brand-primary/5 hover:bg-brand-primary hover:text-brand-bg text-brand-primary flex items-center justify-center transition-all duration-300"
                aria-label="Følg os på Instagram"
              >
                <i className="fa-brands fa-instagram text-base" />
              </a>
              <span className="text-xs text-brand-muted ml-1">Følg os på sociale medier</span>
            </div>
          </div>

        </div>

        {/* Copyright & Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-brand-primary/10 text-center flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-brand-muted">
          <p>© {new Date().getFullYear()} Moon Yoga Fredericia. Alle rettigheder forbeholdes.</p>
          <p className="flex items-center gap-1.5">
            <span>Skabt med</span>
            <i className="fa-solid fa-heart text-brand-primary animate-pulse" />
            <span>for yoga og fællesskab</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
